import { Dispatch, SetStateAction } from 'react';
import { Lead, LeadStages, LeadState, WaitlistedStages } from 'models/Lead';
import { ObjectValues } from 'types';
import { getArrayLength, getIsDesc, getSortBy } from 'utils';
import { DrawerNavigation } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { SELLING_PORTFOLIO_ROUTE } from 'data/global';

export const WAITLISTED_KEYWORD = 'Waitlisted';

export const LEAD_STATE: Record<string, LeadState> = {
  ENQUIRE: 'enquire',
  ENQUIRE_WAITLISTED: 'enquireWaitlisted',
  ACCEPT: 'accept',
  ACCEPT_WAITLISTED: 'acceptWaitlisted',
  CONFIRM: 'confirm',
  CONFIRM_WAITLISTED: 'confirmWaitlisted',
  OFFER: 'offer',
  OFFER_WAITLISTED: 'offerWaitlisted',
} as const;

export const STATE_REORDERING = {
  enquire: 'enquireWaitlisted',
  accept: 'acceptWaitlisted',
  confirm: 'confirmWaitlisted',
  offer: 'offerWaitlisted',
} as const;

export const AUTOMATIC_REORDER = {
  enquireWaitlisted: 'enquire',
  acceptWaitlisted: 'accept',
  confirmWaitlisted: 'confirm',
  offerWaitlisted: 'offer',
} as const;

export const isLeadStage = (value: LeadStages | string): value is LeadStages =>
  !(value as string).includes(WAITLISTED_KEYWORD);

export const isWaitlisted = (
  value: WaitlistedStages | string,
): value is WaitlistedStages => (value as string).includes(WAITLISTED_KEYWORD);

export const STEPPER_LEAD_STAGES = Object.entries(LEAD_STATE).reduce<
  Record<string, LeadStages>
>(
  (acc, [key, value]) =>
    isLeadStage(value)
      ? {
          ...acc,
          [key]: value,
        }
      : acc,
  {} as Record<string, LeadStages>,
);

export const getGridLeadsEmptyState = () =>
  Object.values(LEAD_STATE).reduce<Record<LeadState, []>>(
    (acc, state) => ({ ...acc, [state]: [] }),
    {} as Record<LeadState, []>,
  );

export const WAITLIST_COLUMN_TITLES: Record<WaitlistedStages, string> = {
  enquireWaitlisted: 'inactiveEnquiries',
  acceptWaitlisted: 'inactiveLeads',
  confirmWaitlisted: 'inactiveProspects',
  offerWaitlisted: 'inactiveBidders',
};

export const WAITLIST_COLUMN_POPUP: Record<WaitlistedStages, string> = {
  enquireWaitlisted: 'inactiveEnquiriesPopup',
  acceptWaitlisted: 'inactiveLeadsPopup',
  confirmWaitlisted: 'inactiveProspectsPopup',
  offerWaitlisted: 'inactiveProspectsPopup',
};

export const getLeadsIds = (leads: Lead[]) => leads.map(({ _id }) => _id);

export const getGridLeadsNumber = (gridLeads: Record<string, Lead[]>) =>
  Object.values(gridLeads).reduce(
    (acc, value) => (acc += getArrayLength(value)),
    0,
  );

export const getNumberOfUnreadMessagesNotes = (
  gridLeads: Record<string, Lead[]>,
) =>
  Object.values(gridLeads)
    .flat()
    .reduce(
      (acc, { unreadMessagesCount, unreadNotesCount }) =>
        acc + unreadMessagesCount + unreadNotesCount,
      0,
    );

export const getNumberOfUnrepliedMessagesNotes = (
  gridLeads: Record<string, Lead[]>,
) =>
  Object.values(gridLeads)
    .flat()
    .reduce(
      (
        acc,
        {
          unreadMessagesCount,
          unrepliedMessagesCount,
          unreadNotesCount,
          unrepliedNotesCount,
        },
      ) =>
        acc +
        (unreadMessagesCount ? 0 : unrepliedMessagesCount) +
        (unreadNotesCount ? 0 : unrepliedNotesCount),
      0,
    );

export type LeadsHeaderCounts = {
  chatUnread: number;
  chatUnreplied: number;
  notesUnread: number;
  notesUnreplied: number;
};

// Aggregates per-type header counts (chat = messages, notes = notes) across a
// grid-leads subset, mirroring the per-row cell logic: an "unread" count takes
// priority over "unreplied" for the same lead.
export const getLeadsHeaderCounts = (
  gridLeads: Record<string, Lead[]>,
): LeadsHeaderCounts =>
  Object.values(gridLeads || {})
    .flat()
    .reduce<LeadsHeaderCounts>(
      (
        acc,
        {
          unreadMessagesCount,
          unrepliedMessagesCount,
          unreadNotesCount,
          unrepliedNotesCount,
        },
      ) => ({
        chatUnread: acc.chatUnread + unreadMessagesCount,
        chatUnreplied:
          acc.chatUnreplied +
          (unreadMessagesCount ? 0 : unrepliedMessagesCount),
        notesUnread: acc.notesUnread + unreadNotesCount,
        notesUnreplied:
          acc.notesUnreplied + (unreadNotesCount ? 0 : unrepliedNotesCount),
      }),
      {
        chatUnread: 0,
        chatUnreplied: 0,
        notesUnread: 0,
        notesUnreplied: 0,
      },
    );

const getElementForSort = (element: any, isDate: boolean) =>
  isDate ? +new Date(element) : +element;

const sortElements = (order: number, e1: any, e2: any, isDate = false) =>
  (getElementForSort(e1, isDate) - getElementForSort(e2, isDate)) * order;

export const sortLeads = (leads: Lead[], sort: string) => {
  const order = getIsDesc(sort) ? -1 : 1;
  const sortValue = getSortBy(sort) as keyof Lead;

  const isDate = sortValue === 'lastSeen';

  return [...leads].sort((el1, el2) =>
    sortElements(order, el1[sortValue], el2[sortValue], isDate),
  );
};

export const removeLeadFromArray = (leads: Lead[], leadId: string) =>
  leads.filter(({ _id }) => _id !== leadId);

export const addLeadToArray = (leads: Lead[], newLead: Lead) => [
  ...leads,
  newLead,
];

export const updateLeadInArray = (leads: Lead[], lead: Lead) =>
  leads.map((el) => (el._id === lead._id ? lead : el));

export const updateLeadsInArray = (leads: Lead[], newLeads: Lead[]) =>
  leads.map((el) => {
    const newLead = newLeads?.find(({ _id }) => _id === el?._id);

    return newLead || el;
  });

export const moveLead = (
  newLead: Lead,
  removeFrom: Dispatch<SetStateAction<Lead[]>>,
  addTo: Dispatch<SetStateAction<Lead[]>>,
) => {
  removeFrom((prev) => removeLeadFromArray(prev, newLead._id));
  addTo((prev) => addLeadToArray(prev, newLead));
};

export const LEAD_BACKGROUND_COLORS = {
  GREEN: 'green', // New leads
  CREAM: 'cream', // Highlighted lead
  LIGHT_BLUE: 'light_blue', // Data Room file access never granted
  BLUE: 'blue', // Data Room file access expired
  DARK_BLUE: 'dark_blue', // Data Room file access enabled
  DEFAULT: 'default', // as fallback
} as const;

export type LeadBackgroundColors = ObjectValues<typeof LEAD_BACKGROUND_COLORS>;

export const WITHDRAWN_TO_STAGES: Record<LeadState, WaitlistedStages> = {
  enquire: 'enquireWaitlisted',
  enquireWaitlisted: 'enquireWaitlisted',
  accept: 'acceptWaitlisted',
  acceptWaitlisted: 'acceptWaitlisted',
  confirm: 'confirmWaitlisted',
  confirmWaitlisted: 'confirmWaitlisted',
  offer: 'offerWaitlisted',
  offerWaitlisted: 'offerWaitlisted',
};

export const getIsLeadReleaseOrderPaused = (lead: Lead) =>
  !!lead?.isReleaseOrderPaused && !lead?.withdrawnInterest;

export const getIsLeadReleaseOrderAwaitingReaction = (lead: Lead) =>
  !!lead?.isReleaseOrderAwaitingReaction && !lead?.withdrawnInterest;

export const getIsLeadReleaseOrderWaitingOnBroker = (lead: Lead) =>
  getIsLeadReleaseOrderPaused(lead) ||
  getIsLeadReleaseOrderAwaitingReaction(lead);

export const getGridLeadsWithLead = (
  gridLeads: Record<LeadState, Lead[]>,
  leadId?: string,
) =>
  Object.values(gridLeads).find((leads) =>
    leads.some(({ _id }) => _id === leadId),
  );

export const getActiveDrawerNavigation = (navigations: DrawerNavigation[]) =>
  navigations.find(({ onPrev, onNext }) => onPrev || onNext) ?? {};

export const AGREEMENT_SETUP_PATH = 'nda-setup';
export const AGREEMENT_VIEW_PATH = 'nda';

export type AgreementPageLocationState = { from?: string };

export const getAgreementSetupRoute = (dealRoomId: string) =>
  `${SELLING_PORTFOLIO_ROUTE}/${dealRoomId}/${AGREEMENT_SETUP_PATH}`;

export const getAgreementViewRoute = (dealRoomId: string) =>
  `${SELLING_PORTFOLIO_ROUTE}/${dealRoomId}/${AGREEMENT_VIEW_PATH}`;

export const getAgreementPageBackPath = (state: unknown) =>
  (state as AgreementPageLocationState)?.from || '../deal';
